import React, { useState } from 'react';

const ACCESS_CODE = 'AIScholar2026';
const KIT_FORM_ID = '9338576';

function setCookie(days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = 'aria_verified=true; expires=' + expires + '; path=/; SameSite=Lax';
}

export function isVerified() {
  return document.cookie.split('; ').some(c => c === 'aria_verified=true');
}

const S = {
  page: {
    minHeight: '100vh',
    background: '#0d1117',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: "'DM Sans','Segoe UI',sans-serif",
  },
  card: {
    background: '#161b22',
    border: '1px solid #30363d',
    borderRadius: 16,
    padding: '48px 40px',
    maxWidth: 460,
    width: '100%',
    position: 'relative',
    overflow: 'hidden',
  },
  topBar: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 2,
    background: 'linear-gradient(90deg, #d4a843, transparent)',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 28,
  },
  mark: {
    width: 44, height: 44,
    background: 'rgba(212,168,67,0.12)',
    border: '1px solid rgba(212,168,67,0.3)',
    borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, fontWeight: 800,
    color: '#d4a843',
  },
  logoText: { display: 'flex', flexDirection: 'column' },
  logoName: { fontSize: 18, fontWeight: 800, color: '#f0f6fc', letterSpacing: '0.04em' },
  logoSub: { fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', color: '#d4a843', marginTop: 2 },
  divider: { height: 1, background: '#30363d', margin: '0 0 24px' },
  headline: { fontSize: 24, fontWeight: 800, color: '#f0f6fc', textAlign: 'center', lineHeight: 1.25, marginBottom: 10 },
  subline: { fontSize: 14, color: '#6e7681', textAlign: 'center', lineHeight: 1.7, marginBottom: 28 },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: '#6e7681', marginBottom: 6, display: 'block', textTransform: 'uppercase' },
  input: { width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: 8, padding: '13px 16px', color: '#f0f6fc', fontSize: 16, outline: 'none', fontFamily: 'inherit', marginBottom: 12 },
  btn: (disabled) => ({
    width: '100%', background: disabled ? '#484f58' : '#d4a843',
    color: '#0d1117', border: 'none', borderRadius: 8,
    padding: 14, fontSize: 15, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', marginTop: 4,
  }),
  error: { fontSize: 13, color: '#f85149', textAlign: 'center', marginTop: 10, minHeight: 20 },
  success: { fontSize: 13, color: '#39d353', textAlign: 'center', marginTop: 10, minHeight: 20 },
  features: { marginTop: 24, paddingTop: 20, borderTop: '1px solid #30363d', display: 'flex', flexDirection: 'column', gap: 10 },
  feature: { display: 'flex', gap: 10, fontSize: 13, color: '#6e7681', lineHeight: 1.5 },
  dot: { color: '#d4a843', flexShrink: 0 },
  powered: { marginTop: 20, textAlign: 'center', fontSize: 11, color: '#484f58', lineHeight: 1.6 },
};

export default function Gate({ onVerified }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleEnter = async () => {
    setError('');
    setSuccess('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!code) {
      setError('Please enter your access code.');
      return;
    }
    if (code.trim() !== ACCESS_CODE) {
      setError('That access code is not valid. Please check and try again.');
      return;
    }

    setLoading(true);

    // Try to add to Kit list
    try {
      const kitKey = process.env.REACT_APP_KIT_API_KEY;
      if (kitKey) {
        await fetch(`https://api.convertkit.com/v3/forms/${KIT_FORM_ID}/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ api_key: kitKey, email }),
        });
      }
    } catch (e) {
      // Silent fail — don't block entry if Kit is unavailable
    }

    setCookie(45);
    setSuccess('Welcome! Taking you in...');
    setTimeout(() => onVerified(), 1200);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') handleEnter();
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.topBar} />

        <div style={S.logo}>
          <div style={S.mark}>A</div>
          <div style={S.logoText}>
            <span style={S.logoName}>ARIA AI Academy</span>
            <span style={S.logoSub}>AI Coaching Lab</span>
          </div>
        </div>

        <div style={S.divider} />

        <div style={S.headline}>Enter ARIA's AI Coaching Lab</div>
        <div style={S.subline}>
          Your 24/7 conversational AI coach — powered by Claude AI.<br />
          Enter your email and access code to begin.
        </div>

        <label style={S.label}>Your Email</label>
        <input
          style={S.input}
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={handleKey}
        />

        <label style={S.label}>Access Code</label>
        <input
          style={S.input}
          type="text"
          placeholder="Enter your access code"
          value={code}
          onChange={e => setCode(e.target.value)}
          onKeyDown={handleKey}
        />

        <button style={S.btn(loading)} onClick={handleEnter} disabled={loading}>
          {loading ? 'One moment...' : 'Enter the Lab →'}
        </button>

        {error && <div style={S.error}>{error}</div>}
        {success && <div style={S.success}>{success}</div>}

        <div style={S.features}>
          <div style={S.feature}><span style={S.dot}>✦</span>24/7 AI coaching — ask anything, anytime</div>
          <div style={S.feature}><span style={S.dot}>✦</span>Learn AI through real conversation — no videos, no lectures</div>
          <div style={S.feature}><span style={S.dot}>✦</span>Practical guidance for your real work and business</div>
          <div style={S.feature}><span style={S.dot}>✦</span>Always current — powered by Claude AI, built by Anthropic</div>
        </div>

        <div style={S.powered}>
          Powered by Claude AI · Built by Anthropic<br />
          Founded by Deborah Kirkpatrick · Chief AI Conversation Strategist
        </div>
      </div>
    </div>
  );
}
